import { HttpStatusCode, isAxiosError, isCancel } from "axios";
import { SendToTrackingService } from "@/api/handlers/errors";
import { useApiClient } from "@/hooks/useApiClient";
import {
  type GeoSpaceChiban,
  type GeoSpacePolygonItem,
} from "@/features/map/types";
import {
  type RegistrationMapDetail,
  type RegistrationMapLookupResponse,
} from "@/features/map/types/registrationMap";

/** 範囲検索の結果。上限件数で打ち切られたかを results と一緒に返す。 */
export interface RegistrationMapShapeResult {
  results: GeoSpaceChiban[];
  /** 該当筆が BE の返却上限を超え、results が上限件数で打ち切られたか。 */
  truncated: boolean;
}

/**
 * 地番・住所・座標の検索 3 本（search / reverse / reverse-latlng）は、BE が Google を
 * 呼ぶためユーザー単位のレート制限（3 本の合計）を掛けている。超えると 429 が返る。
 */
export const isRegistrationMapRateLimitError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response?.status === HttpStatusCode.TooManyRequests;

/**
 * 地番・住所・座標の検索 3 本は、通信失敗時に undefined を返す（検索チェーンではミス扱い）。
 * ただしレート制限（429）だけは、後段の有料ソースへ流さず検索を止めるため例外を投げる。
 */
interface RegistrationMapApi {
  search: (
    chiban: string
  ) => Promise<RegistrationMapLookupResponse | undefined>;
  reverse: (
    address: string
  ) => Promise<RegistrationMapLookupResponse | undefined>;
  reverseByLatLng: (
    lat: number,
    lng: number
  ) => Promise<RegistrationMapLookupResponse | undefined>;
  /** 範囲内の筆一覧。通信に失敗したときは 0 件と区別するため undefined を返す。 */
  reverseByPolygon: (
    polygon: string,
    signal?: AbortSignal
  ) => Promise<RegistrationMapShapeResult | undefined>;
  /** 範囲内の筆一覧。通信に失敗したときは 0 件と区別するため undefined を返す。 */
  reverseByCircle: (
    radius: number,
    lat: number,
    lng: number,
    signal?: AbortSignal
  ) => Promise<RegistrationMapShapeResult | undefined>;
}

/**
 * 登記所備付地図 API の失敗をエラー監視へ送る。
 *
 * 検索チェーンでは失敗をミスとして扱い後段のソースへ流すため、利用者には見えない。
 * 障害に気付けるよう 500 以上と通信断を送り、中断（範囲検索のキャンセル）と
 * 入力起因の 4xx は送らない。レート制限（429）は機械的な連打や上限の過不足に
 * 気付けるよう送る。
 */
export const reportRegistrationMapError = (error: unknown): void => {
  if (isCancel(error) || !isAxiosError(error)) return;
  const status = error.response?.status ?? HttpStatusCode.InternalServerError;
  if (
    status >= HttpStatusCode.InternalServerError ||
    status === HttpStatusCode.TooManyRequests
  ) {
    SendToTrackingService(error);
  }
};

/** reverse-polygon / reverse-circle（範囲検索）のレスポンス。 */
interface RegistrationMapShapeResponse {
  results: RegistrationMapDetail[];
  truncated: boolean;
}

const toShapeResult = ({
  results,
  truncated,
}: RegistrationMapShapeResponse): RegistrationMapShapeResult => ({
  results: results.map(registrationMapDetailToGeoSpaceChiban),
  truncated,
});

const toPolygonItems = (
  detail: RegistrationMapDetail
): GeoSpacePolygonItem[][][] => {
  const { type, coordinates } = detail.geometry;
  if (!Array.isArray(coordinates)) return [];
  const polygons: unknown[] =
    type === "MultiPolygon"
      ? coordinates
      : type === "Polygon"
      ? [coordinates]
      : [];

  return polygons.flatMap((polygon) => {
    if (!Array.isArray(polygon)) return [];
    const rings = polygon.map((ring: unknown): GeoSpacePolygonItem[] => {
      if (!Array.isArray(ring)) return [];
      return ring.flatMap((coordinate: unknown) => {
        if (!Array.isArray(coordinate)) return [];
        const [lng, lat] = coordinate as unknown[];
        return typeof lat === "number" &&
          typeof lng === "number" &&
          Number.isFinite(lat) &&
          Number.isFinite(lng)
          ? [{ lat, lng }]
          : [];
      });
    });
    // 外周がないポリゴンは表示できない。穴の順序と区画の階層は保持する。
    return rings[0]?.length ? [rings] : [];
  });
};

const normalizeAdministrativeName = (value: string): string =>
  value.replace(/[ヶケヵ]/g, "ケ");

const startsWithAdministrativeName = (
  location: string,
  administrativeName: string
): boolean =>
  normalizeAdministrativeName(location).startsWith(
    normalizeAdministrativeName(administrativeName)
  );

const removeAdministrativePrefix = (
  location: string,
  administrativeName: string
): string =>
  startsWithAdministrativeName(location, administrativeName)
    ? location.slice(administrativeName.length)
    : location;

/** 登記所備付地図の詳細を既存の地番検索結果表示へ渡せる形式に変換する。 */
export const registrationMapDetailToGeoSpaceChiban = (
  detail: RegistrationMapDetail
): GeoSpaceChiban => {
  const locationWithoutPrefName = removeAdministrativePrefix(
    detail.location,
    detail.prefName
  );
  const locationIncludesCityName = startsWithAdministrativeName(
    locationWithoutPrefName,
    detail.cityName
  );
  const ooazaName = locationIncludesCityName
    ? locationWithoutPrefName.slice(detail.cityName.length)
    : locationWithoutPrefName;
  const locationWithCityName = locationIncludesCityName
    ? locationWithoutPrefName
    : `${detail.cityName}${locationWithoutPrefName}`;
  const chiban = detail.chiban ?? "";
  const polygonItems = toPolygonItems(detail);

  return {
    prefName: detail.prefName,
    cityName: detail.cityName,
    ooazaName: ooazaName || null,
    azaName: null,
    chiban,
    addressChiban: `${detail.prefName}${locationWithCityName}${chiban}`,
    polygon:
      polygonItems.length > 0
        ? { type: detail.geometry.type, coordinates: polygonItems }
        : undefined,
    lat: detail.lat,
    lng: detail.lng,
    address: detail.addressResidence,
    area: detail.area,
  };
};

/** 登記所備付地図のクリック座標から筆詳細を取得する。 */
export const useRegistrationMapApi = (): RegistrationMapApi => {
  const { apiClient } = useApiClient();

  const search = async (
    chiban: string
  ): Promise<RegistrationMapLookupResponse | undefined> => {
    try {
      const response = await apiClient.get<RegistrationMapLookupResponse>(
        "/trustart/registration-map/search",
        { params: { chiban } }
      );
      return response.data;
    } catch (error) {
      reportRegistrationMapError(error);
      if (isRegistrationMapRateLimitError(error)) throw error;
      return undefined;
    }
  };

  const reverse = async (
    address: string
  ): Promise<RegistrationMapLookupResponse | undefined> => {
    try {
      const response = await apiClient.get<RegistrationMapLookupResponse>(
        "/trustart/registration-map/reverse",
        { params: { address } }
      );
      return response.data;
    } catch (error) {
      reportRegistrationMapError(error);
      if (isRegistrationMapRateLimitError(error)) throw error;
      return undefined;
    }
  };

  const reverseByLatLng = async (
    lat: number,
    lng: number
  ): Promise<RegistrationMapLookupResponse | undefined> => {
    try {
      const response = await apiClient.get<RegistrationMapLookupResponse>(
        "/trustart/registration-map/reverse-latlng",
        { params: { lat, lng } }
      );
      return response.data;
    } catch (error) {
      reportRegistrationMapError(error);
      if (isRegistrationMapRateLimitError(error)) throw error;
      return undefined;
    }
  };

  const reverseByPolygon = async (
    polygon: string,
    signal?: AbortSignal
  ): Promise<RegistrationMapShapeResult | undefined> => {
    try {
      const response = await apiClient.get<RegistrationMapShapeResponse>(
        "/trustart/registration-map/reverse-polygon",
        { params: { polygon }, signal }
      );
      return toShapeResult(response.data);
    } catch (error) {
      reportRegistrationMapError(error);
      return undefined;
    }
  };

  const reverseByCircle = async (
    radius: number,
    lat: number,
    lng: number,
    signal?: AbortSignal
  ): Promise<RegistrationMapShapeResult | undefined> => {
    try {
      const response = await apiClient.get<RegistrationMapShapeResponse>(
        "/trustart/registration-map/reverse-circle",
        { params: { radius, lat, lng }, signal }
      );
      return toShapeResult(response.data);
    } catch (error) {
      reportRegistrationMapError(error);
      return undefined;
    }
  };

  return {
    search,
    reverse,
    reverseByLatLng,
    reverseByPolygon,
    reverseByCircle,
  };
};
